import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { fetchLink, updateLink, deleteLink, publishLink, type Link, type MeResponse } from '../lib/api';

export default function LinkDetail({ me }: { me: MeResponse | null }) {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const isAdmin = !!me?.is_admin;
  const [link, setLink] = useState<Link | null>(null);
  const [destination, setDestination] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!id) return;
    // Try mine first, then admin scope if 404/403
    fetchLink(id, false)
      .catch(() => (isAdmin ? fetchLink(id, true) : Promise.reject(new Error('not found'))))
      .then((r) => {
        setLink(r.data);
        setDestination(r.data.destination ?? '');
      })
      .catch((e) => setError(e.message));
  }, [id, isAdmin]);

  async function save() {
    if (!link) return;
    setBusy(true);
    setError(null);
    try {
      const useAdmin = isAdmin && link.user_id !== me?.user_id;
      const r = await updateLink(link.id, { destination }, useAdmin);
      setLink(r.data);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function publish(opts: { asFree?: boolean } = {}) {
    if (!link) return;
    setBusy(true);
    setError(null);
    try {
      await publishLink(link.id, destination, opts);
      const useAdmin = isAdmin && link.user_id !== me?.user_id;
      const r = await fetchLink(link.id, useAdmin);
      setLink(r.data);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!link) return;
    if (!confirm(`Delete ${link.id}?`)) return;
    setBusy(true);
    try {
      const useAdmin = isAdmin && link.user_id !== me?.user_id;
      await deleteLink(link.id, useAdmin);
      nav('/');
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  }

  if (error) return <p className="text-red-600">{error}</p>;
  if (!link) return <p className="text-gray-500">Loading…</p>;

  return (
    <div className="max-w-2xl">
      <button onClick={() => nav('/')} className="text-sm text-gray-500 hover:text-gray-900 mb-6">
        ← Back
      </button>

      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h1 className="font-mono text-xl">{link.id}</h1>
          {link.active ? (
            <span className="text-green-700 bg-green-100 px-2 py-0.5 rounded text-xs">live</span>
          ) : (
            <span className="text-gray-500 bg-gray-100 px-2 py-0.5 rounded text-xs">draft</span>
          )}
        </div>

        <div className="flex gap-6 items-start">
          <div className="bg-white border border-gray-200 rounded-lg p-3 shrink-0">
            <QRCodeSVG value={`https://linkr.fyi/q/${link.id}`} size={160} level="M" includeMargin={false} />
          </div>
          <dl className="text-sm space-y-2 flex-1">
            <Row k="Type" v={link.type} />
            <Row k="QR target" v={`linkr.fyi/q/${link.id}`} mono />
            {isAdmin && link.user_id !== me?.user_id && <Row k="Owner" v={link.user_id} mono />}
            <Row k="Tier" v={link.active ? (link.branded ? 'free (branded)' : 'paid (clean)') : '—'} />
            <Row k="Scans" v={String(link.scanCount ?? 0)} />
            <Row k="Prepaid until" v={link.prepaidUntil ?? '—'} />
          </dl>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <h2 className="font-medium mb-2">Test redirect</h2>
        <p className="text-xs text-gray-500 mb-3">
          Active + paid → 302 to destination. Active + overdue → paywall page. Missing → "not active" page.
        </p>
        <a
          href={`https://linkr.fyi/q/${link.id}`}
          target="_blank"
          rel="noreferrer"
          className="inline-block bg-gray-900 text-white text-sm rounded-lg px-4 py-2 hover:bg-gray-700"
        >
          Open linkr.fyi/q/{link.id} →
        </a>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <h2 className="font-medium mb-3">Destination</h2>
        <input
          type="text"
          value={destination}
          onChange={(e) => setDestination(e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
        />
        <div className="flex gap-2 mt-3">
          <button
            onClick={save}
            disabled={busy}
            className="bg-gray-900 text-white text-sm rounded-lg px-4 py-2 hover:bg-gray-700 disabled:opacity-50"
          >
            Save
          </button>
          <button
            onClick={() => publish()}
            disabled={busy}
            className="bg-blue-600 text-white text-sm rounded-lg px-4 py-2 hover:bg-blue-700 disabled:opacity-50"
          >
            Publish
          </button>
          {isAdmin && (
            <button
              onClick={() => publish({ asFree: true })}
              disabled={busy}
              className="bg-purple-600 text-white text-sm rounded-lg px-4 py-2 hover:bg-purple-700 disabled:opacity-50"
              title="Force branded interstitial regardless of subscription — for testing free-tier flow"
            >
              Publish as free (test)
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="font-medium mb-2">Payload</h2>
        <pre className="bg-gray-50 rounded-lg p-3 text-xs overflow-auto">{JSON.stringify(link.payload, null, 2)}</pre>
      </div>

      <div className="mt-8 text-right">
        <button onClick={remove} disabled={busy} className="text-sm text-red-600 hover:text-red-800">
          Delete link
        </button>
      </div>
    </div>
  );
}

function Row({ k, v, mono }: { k: string; v: string; mono?: boolean }) {
  return (
    <div className="flex">
      <dt className="w-32 text-gray-500">{k}</dt>
      <dd className={`flex-1 text-gray-900 ${mono ? 'font-mono text-xs' : ''}`}>{v}</dd>
    </div>
  );
}
